import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, Paperclip, Trash2, Upload } from 'lucide-react';
import {
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import {
  createEpisodeAuthorization,
  deleteEpisodeAuthorization,
  getPatientDocumentUrl,
  updateEpisodeAuthorization,
  uploadPatientDocument,
} from '../../services/api';
import type {
  AuthorizationCreate,
  AuthorizationResponse,
  AuthorizationStatus,
  VisitResponse,
} from '../../services/api';
import type { PatientInsuranceResponse } from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import {
  formatDateToISO,
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import { scrollToField } from '../../utils/scrollToField';
import { AlertTriangle } from 'lucide-react';
import { AUTHORIZATION_STATUS_OPTIONS } from './authorizationConstants';

interface AuthorizationDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  episodeId: string;
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
  patientInsurances: PatientInsuranceResponse[];
  defaultInsuranceId?: string | null;
  editingAuthorization?: AuthorizationResponse | null;
  /** Episode visits, used to warn when an edit would break linked-visit checks. */
  episodeVisits?: VisitResponse[];
  onSaved: () => void;
}

// Maps a visit discipline to its authorized-visits field key for warnings.
const DISCIPLINE_TO_VISIT_FIELD: Record<string, VisitCountField['key']> = {
  SN: 'sn_visits',
  PT: 'pt_visits',
  OT: 'ot_visits',
  ST: 'st_visits',
  MSW: 'msw_visits',
  HHA: 'hha_visits',
};

const FieldLabel: React.FC<{
  children: React.ReactNode;
  required?: boolean;
}> = ({ children, required }) => (
  <p className='text-sm font-medium mb-1.5'>
    {children}
    {required && <span className='text-destructive ml-0.5'>*</span>}
  </p>
);

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const INPUT_ERROR_CLASS = INPUT_CLASS.replace(
  'border-input',
  'border-destructive'
);

interface VisitCountField {
  key:
    | 'sn_visits'
    | 'pt_visits'
    | 'ot_visits'
    | 'st_visits'
    | 'msw_visits'
    | 'hha_visits'
    | 'dietician_visits'
    | 'misc_visits';
  label: string;
}

const VISIT_COUNT_FIELDS: VisitCountField[] = [
  { key: 'sn_visits', label: 'SN Visits' },
  { key: 'pt_visits', label: 'PT Visits' },
  { key: 'ot_visits', label: 'OT Visits' },
  { key: 'st_visits', label: 'ST Visits' },
  { key: 'msw_visits', label: 'MSW Visits' },
  { key: 'hha_visits', label: 'HHA Visits' },
  { key: 'dietician_visits', label: 'Dietician Visits' },
  { key: 'misc_visits', label: 'Misc Visits' },
];

type VisitCounts = Record<VisitCountField['key'], string>;

const DEFAULT_VISIT_COUNTS: VisitCounts = {
  sn_visits: '0',
  pt_visits: '0',
  ot_visits: '0',
  st_visits: '0',
  msw_visits: '0',
  hha_visits: '0',
  dietician_visits: '0',
  misc_visits: '0',
};

const parseCount = (value: string): number | null => {
  if (value.trim() === '') return null;
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 ? n : null;
};

// A non-empty value that fails to parse is a hard error; empty means "unset",
// which is allowed. The old behavior silently coerced invalid input to null,
// dropping the user's intent without telling them — mirror the backend's
// Field(ge=0) constraints by surfacing the error and blocking save instead.
const isInvalidCount = (value: string): boolean =>
  value.trim() !== '' && parseCount(value) === null;

export const AuthorizationDialog: React.FC<AuthorizationDialogProps> = ({
  open,
  onClose,
  patientId,
  episodeId,
  episodeStartDate,
  episodeEndDate,
  patientInsurances,
  defaultInsuranceId,
  editingAuthorization,
  episodeVisits = [],
  onSaved,
}) => {
  const { success: showSuccess, error: showError } = useToast();
  const isEditing = Boolean(editingAuthorization);

  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [status, setStatus] = useState<AuthorizationStatus>('NOT_STARTED');
  const [patientInsuranceId, setPatientInsuranceId] = useState('');
  const [authNumber, setAuthNumber] = useState('');
  const [visitCounts, setVisitCounts] =
    useState<VisitCounts>(DEFAULT_VISIT_COUNTS);
  const [comment, setComment] = useState('');

  // Attachment: existing document on the record, a newly picked file, or a
  // pending removal of the existing one.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [removeDocument, setRemoveDocument] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const insuranceOptions = useMemo(
    () =>
      patientInsurances.map(ins => ({
        value: ins.id,
        label: ins.insurance_name,
      })),
    [patientInsurances]
  );

  const episodeStart = useMemo(
    () => parseISOToLocalDate(episodeStartDate ?? undefined) ?? undefined,
    [episodeStartDate]
  );
  const episodeEnd = useMemo(
    () => parseISOToLocalDate(episodeEndDate ?? undefined) ?? undefined,
    [episodeEndDate]
  );

  const resetForm = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setStatus('NOT_STARTED');
    setPatientInsuranceId(defaultInsuranceId ?? '');
    setAuthNumber('');
    setVisitCounts(DEFAULT_VISIT_COUNTS);
    setComment('');
    setPendingFile(null);
    setRemoveDocument(false);
  };

  // Initialize form on open (prefill when editing, defaults when creating)
  useEffect(() => {
    if (!open) return;
    if (!editingAuthorization) {
      resetForm();
      // Prefill new authorizations with the episode's date range.
      setStartDate(episodeStart);
      setEndDate(episodeEnd);
      return;
    }
    const a = editingAuthorization;
    setStartDate(parseISOToLocalDate(a.start_date) ?? undefined);
    setEndDate(parseISOToLocalDate(a.end_date) ?? undefined);
    setStatus(a.status ?? 'NOT_STARTED');
    setPatientInsuranceId(a.patient_insurance_id ?? '');
    setAuthNumber(a.authorization_number ?? '');
    setVisitCounts({
      sn_visits: a.sn_visits?.toString() ?? '',
      pt_visits: a.pt_visits?.toString() ?? '',
      ot_visits: a.ot_visits?.toString() ?? '',
      st_visits: a.st_visits?.toString() ?? '',
      msw_visits: a.msw_visits?.toString() ?? '',
      hha_visits: a.hha_visits?.toString() ?? '',
      dietician_visits: a.dietician_visits?.toString() ?? '',
      misc_visits: a.misc_visits?.toString() ?? '',
    });
    setComment(a.comment ?? '');
    setPendingFile(null);
    setRemoveDocument(false);
  }, [open, editingAuthorization, episodeStart, episodeEnd]);

  const startDateOutOfEpisodeRange = Boolean(
    startDate &&
      ((episodeStart && startDate < episodeStart) ||
        (episodeEnd && startDate > episodeEnd))
  );
  const endDateOutOfEpisodeRange = Boolean(
    endDate &&
      ((episodeStart && endDate < episodeStart) ||
        (episodeEnd && endDate > episodeEnd))
  );

  // Per-field hard errors: a non-empty visit count that isn't a whole number >= 0.
  // These block save (below), matching the backend's Field(ge=0) constraints. Note
  // this is deliberately distinct from the non-blocking `validationWarnings`
  // (used > authorized), which stay soft.
  const fieldErrors = useMemo(() => {
    const errs: Partial<Record<VisitCountField['key'], string>> = {};
    for (const field of VISIT_COUNT_FIELDS) {
      if (isInvalidCount(visitCounts[field.key])) {
        errs[field.key] = 'Enter a whole number of visits (0 or more).';
      }
    }
    return errs;
  }, [visitCounts]);
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;

  const isValid = useMemo(() => {
    if (!startDate || !endDate) return false;
    if (endDate < startDate) return false;
    if (startDateOutOfEpisodeRange || endDateOutOfEpisodeRange) return false;
    if (hasFieldErrors) return false;
    return true;
  }, [
    startDate,
    endDate,
    startDateOutOfEpisodeRange,
    endDateOutOfEpisodeRange,
    hasFieldErrors,
  ]);

  // Visits currently linked to the auth being edited (for soft warnings).
  const linkedVisits = useMemo(
    () =>
      editingAuthorization
        ? episodeVisits.filter(
            v => v.authorization_id === editingAuthorization.id
          )
        : [],
    [editingAuthorization, episodeVisits]
  );

  // Non-blocking warnings: saving is still allowed, but these edits would make
  // the episode's authorization checks fail. Each links back to its field.
  const validationWarnings = useMemo(() => {
    const out: { message: string; anchor: string }[] = [];
    if (startDate && endDate) {
      for (const v of linkedVisits) {
        const d = parseISOToLocalDate(v.visit_date);
        if (d && (d < startDate || d > endDate)) {
          out.push({
            message: `${v.discipline ?? 'Visit'} visit on ${formatDateToMMDDYYYY(
              v.visit_date
            )} falls outside these authorization dates`,
            anchor: 'authdlg-dates',
          });
        }
      }
    }
    const linkedByDiscipline: Record<string, number> = {};
    for (const v of linkedVisits) {
      if (v.discipline) {
        linkedByDiscipline[v.discipline] =
          (linkedByDiscipline[v.discipline] ?? 0) + 1;
      }
    }
    for (const [discipline, count] of Object.entries(linkedByDiscipline)) {
      const fieldKey = DISCIPLINE_TO_VISIT_FIELD[discipline];
      if (!fieldKey) continue;
      const allowed = parseCount(visitCounts[fieldKey]) ?? 0;
      if (count > allowed) {
        out.push({
          message: `${count} ${discipline} visit${count === 1 ? '' : 's'} linked, but only ${allowed} authorized`,
          anchor: `authdlg-${fieldKey}`,
        });
      }
    }
    return out;
  }, [linkedVisits, startDate, endDate, visitCounts]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleViewDocument = async () => {
    if (!editingAuthorization?.document_id) return;
    try {
      const res = await getPatientDocumentUrl({
        path: {
          patient_id: patientId,
          document_id: editingAuthorization.document_id,
        },
      });
      if (res.error) {
        console.error('Failed to get document URL:', res.error);
        showError('Error', 'Unable to open attachment.');
        return;
      }
      const url = (res.data as { url?: string } | undefined)?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Failed to get document URL:', err);
      showError('Error', 'Unable to open attachment.');
    }
  };

  const handleSave = async () => {
    if (!isValid || !startDate || !endDate) return;
    setSaving(true);
    try {
      // Upload new attachment first so we can link it on the record
      let documentId: string | null | undefined = undefined;
      if (pendingFile) {
        const uploadRes = await uploadPatientDocument({
          path: { patient_id: patientId },
          query: { display_name: pendingFile.name },
          body: { file: pendingFile },
        });
        if (uploadRes.error || !uploadRes.data) {
          console.error('Failed to upload attachment:', uploadRes.error);
          showError('Upload Failed', 'Failed to upload attachment.');
          return;
        }
        documentId = uploadRes.data.id;
      } else if (removeDocument) {
        documentId = null;
      }

      const body: AuthorizationCreate = {
        start_date: formatDateToISO(startDate),
        end_date: formatDateToISO(endDate),
        status,
        patient_insurance_id: patientInsuranceId || null,
        authorization_number: authNumber.trim() || null,
        sn_visits: parseCount(visitCounts.sn_visits),
        pt_visits: parseCount(visitCounts.pt_visits),
        ot_visits: parseCount(visitCounts.ot_visits),
        st_visits: parseCount(visitCounts.st_visits),
        msw_visits: parseCount(visitCounts.msw_visits),
        hha_visits: parseCount(visitCounts.hha_visits),
        dietician_visits: parseCount(visitCounts.dietician_visits),
        misc_visits: parseCount(visitCounts.misc_visits),
        comment: comment.trim() || null,
        ...(documentId !== undefined ? { document_id: documentId } : {}),
      };

      let res;
      if (isEditing && editingAuthorization) {
        res = await updateEpisodeAuthorization({
          path: {
            episode_id: episodeId,
            authorization_id: editingAuthorization.id,
          },
          body,
        });
      } else {
        res = await createEpisodeAuthorization({
          path: { episode_id: episodeId },
          body,
        });
      }
      if (res.error) {
        console.error('Failed to save authorization:', res.error);
        showError('Save Failed', 'Failed to save authorization.');
        return;
      }
      showSuccess(
        'Authorization Saved',
        isEditing ? 'Authorization updated.' : 'Authorization created.'
      );
      handleClose();
      onSaved();
    } catch (err) {
      console.error('Error saving authorization:', err);
      showError('Save Failed', 'Failed to save authorization.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingAuthorization) return;
    setDeleting(true);
    try {
      const res = await deleteEpisodeAuthorization({
        path: {
          episode_id: episodeId,
          authorization_id: editingAuthorization.id,
        },
      });
      if (res.error) {
        console.error('Failed to delete authorization:', res.error);
        showError('Error', 'Failed to delete authorization.');
        return;
      }
      showSuccess('Deleted', 'Authorization deleted.');
      handleClose();
      onSaved();
    } catch (err) {
      console.error('Failed to delete authorization:', err);
      showError('Error', 'Failed to delete authorization.');
    } finally {
      setDeleting(false);
    }
  };

  const existingFileName =
    !pendingFile && !removeDocument
      ? (editingAuthorization?.document_file_name ?? null)
      : null;

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        if (!o) handleClose();
      }}
    >
      <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Authorization' : 'Add Authorization'}
          </DialogTitle>
          <DialogDescription className='sr-only'>
            Record an insurance authorization for this episode.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-5'>
          {/* Non-blocking validation warnings (saving still allowed) */}
          {validationWarnings.length > 0 && (
            <div className='rounded-md border border-destructive/40 bg-destructive/5 p-3'>
              <div className='flex items-center gap-1.5 text-sm font-medium text-destructive'>
                <AlertTriangle className='h-4 w-4 shrink-0' />
                This authorization won&apos;t cover all of its linked visits
              </div>
              <ul className='mt-1.5 space-y-1'>
                {validationWarnings.map((w, i) => (
                  <li key={`${w.anchor}-${i}`}>
                    <button
                      type='button'
                      onClick={() => scrollToField(w.anchor, { focus: false })}
                      className='text-left text-xs text-destructive underline-offset-2 hover:underline'
                    >
                      {w.message}
                    </button>
                  </li>
                ))}
              </ul>
              <p className='mt-1.5 text-xs text-muted-foreground'>
                You can still save — these visits will show as authorization
                errors until resolved.
              </p>
            </div>
          )}

          {/* Dates */}
          <div
            data-field-anchor='authdlg-dates'
            className='grid grid-cols-1 sm:grid-cols-2 gap-4'
          >
            <div>
              <FieldLabel required>Start Date</FieldLabel>
              <DatePicker
                date={startDate}
                onDateChange={setStartDate}
                minDate={episodeStart}
                maxDate={episodeEnd}
              />
              {startDateOutOfEpisodeRange && (
                <p className='mt-1 text-xs text-destructive'>
                  Start date must fall within the episode dates.
                </p>
              )}
            </div>
            <div>
              <FieldLabel required>End Date</FieldLabel>
              <DatePicker
                date={endDate}
                onDateChange={setEndDate}
                minDate={episodeStart}
                maxDate={episodeEnd}
              />
              {startDate && endDate && endDate < startDate && (
                <p className='mt-1 text-xs text-destructive'>
                  End date must be on or after start date.
                </p>
              )}
              {endDateOutOfEpisodeRange && (
                <p className='mt-1 text-xs text-destructive'>
                  End date must fall within the episode dates.
                </p>
              )}
            </div>
          </div>

          {/* Status + Insurance */}
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <div>
              <FieldLabel>Status</FieldLabel>
              <Select
                value={status}
                onValueChange={v => setStatus(v as AuthorizationStatus)}
              >
                <SelectTrigger>
                  <SelectValue placeholder='Select status…' />
                </SelectTrigger>
                <SelectContent>
                  {AUTHORIZATION_STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <FieldLabel>Insurance</FieldLabel>
              <Select
                value={patientInsuranceId}
                onValueChange={setPatientInsuranceId}
              >
                <SelectTrigger>
                  <SelectValue placeholder='Select insurance…' />
                </SelectTrigger>
                <SelectContent>
                  {insuranceOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Authorization Number */}
          <div>
            <FieldLabel>Authorization Number</FieldLabel>
            <input
              type='text'
              value={authNumber}
              onChange={e => setAuthNumber(e.target.value)}
              placeholder='Payer-issued authorization number'
              className={INPUT_CLASS}
            />
          </div>

          {/* Authorized Visits */}
          <div>
            <FieldLabel>Authorized Visits</FieldLabel>
            <div className='grid grid-cols-2 sm:grid-cols-4 gap-3'>
              {VISIT_COUNT_FIELDS.map(field => (
                <div key={field.key} data-field-anchor={`authdlg-${field.key}`}>
                  <p className='text-xs text-muted-foreground mb-1'>
                    {field.label}
                  </p>
                  <input
                    type='number'
                    min={0}
                    step={1}
                    value={visitCounts[field.key]}
                    onChange={e =>
                      setVisitCounts(prev => ({
                        ...prev,
                        [field.key]: e.target.value,
                      }))
                    }
                    placeholder='—'
                    aria-invalid={Boolean(fieldErrors[field.key])}
                    className={
                      fieldErrors[field.key] ? INPUT_ERROR_CLASS : INPUT_CLASS
                    }
                  />
                  {fieldErrors[field.key] && (
                    <p className='mt-1 text-xs text-destructive'>
                      {fieldErrors[field.key]}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div>
            <FieldLabel>Comment</FieldLabel>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder='Notes about this authorization…'
              rows={3}
              className={`${INPUT_CLASS} resize-none`}
            />
          </div>

          {/* Attachment */}
          <div>
            <FieldLabel>Attachment</FieldLabel>
            <input
              ref={fileInputRef}
              type='file'
              className='hidden'
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) {
                  setPendingFile(file);
                  setRemoveDocument(false);
                }
                e.target.value = '';
              }}
            />
            {pendingFile ? (
              <div className='flex items-center gap-2 text-sm'>
                <Paperclip className='h-4 w-4 text-muted-foreground' />
                <span className='truncate'>{pendingFile.name}</span>
                <span className='text-xs text-muted-foreground'>
                  (will upload on save)
                </span>
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  aria-label='Remove selected file'
                  onClick={() => setPendingFile(null)}
                >
                  <Trash2 className='h-4 w-4' />
                </Button>
              </div>
            ) : existingFileName ? (
              <div className='flex items-center gap-2 text-sm'>
                <Paperclip className='h-4 w-4 text-muted-foreground' />
                <span className='truncate'>{existingFileName}</span>
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  aria-label='Open attachment'
                  onClick={handleViewDocument}
                >
                  <ExternalLink className='h-4 w-4' />
                </Button>
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  aria-label='Remove attachment'
                  onClick={() => setRemoveDocument(true)}
                >
                  <Trash2 className='h-4 w-4' />
                </Button>
              </div>
            ) : (
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className='h-3.5 w-3.5 mr-1' />
                Upload File
              </Button>
            )}
            {removeDocument && !pendingFile && (
              <p className='mt-1 text-xs text-muted-foreground'>
                Attachment will be removed on save.{' '}
                <button
                  type='button'
                  className='underline'
                  onClick={() => setRemoveDocument(false)}
                >
                  Undo
                </button>
              </p>
            )}
          </div>
        </div>

        <DialogFooter className='gap-2 mt-2'>
          {isEditing && (
            <Button
              variant='destructive'
              onClick={handleDelete}
              disabled={saving || deleting}
              className='mr-auto'
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          )}
          <Button
            variant='outline'
            onClick={handleClose}
            disabled={saving || deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isValid || saving || deleting}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
